import React, { useEffect, useState } from "react";
import { Link, NavLink, Outlet, useNavigate } from "react-router-dom";
import mcbLogo from "../images/mcb-logo.png";
import {
  ClipboardList,
  PlusCircle,
  Ship,
  UserCircle,
  LogOut,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";

const API_BASE = process.env.REACT_APP_API_URL || "http://localhost:4040/api";
const API = `${API_BASE}/customer-auth`;

const NAV_ITEMS = [
  { label: "My Bookings", to: "/portal", icon: ClipboardList, end: true },
  { label: "New Request", to: "/portal/new", icon: PlusCircle },
  { label: "Sailing Schedule", to: "/portal/sailings", icon: Ship },
  { label: "My Profile", to: "/portal/profile", icon: UserCircle },
];

export default function CustomerPortalLayout() {
  const navigate = useNavigate();
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [customer, setCustomer] = useState(null);
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem("customerToken");
    if (!token) {
      navigate("/portal/login");
      return;
    }
    (async () => {
      try {
        const res = await fetch(`${API}/me`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) {
          localStorage.removeItem("customerToken");
          navigate("/portal/login");
          return;
        }
        const data = await res.json();
        setCustomer(data.customer);
      } catch (err) {
        console.error(err);
        navigate("/portal/login");
      } finally {
        setChecking(false);
      }
    })();
  }, []);

  const handleLogout = () => {
    localStorage.removeItem("customerToken");
    localStorage.removeItem("customer");
    navigate("/portal/login");
  };

  if (checking) {
    return (
      <div className="h-screen bg-gray-50 flex items-center justify-center text-sm text-gray-400">
        Loading your portal...
      </div>
    );
  }

  if (!customer) return null;

  return (
    <div className="h-screen bg-gray-50 flex overflow-hidden">
      <aside
        className={`h-screen border-r border-gray-200 bg-white flex flex-col transition-all duration-200 ${
          isCollapsed ? "w-[64px]" : "w-[240px]"
        }`}
      >
        {/* Header */}
        <div className="h-16 px-3 border-b border-gray-200 flex items-center justify-between shrink-0">
          {!isCollapsed && (
            <Link to="/portal" className="flex flex-1 min-w-0 items-center justify-center">
              <img src={mcbLogo} alt="McCallum Bros Ltd" className="h-8 w-auto max-w-full" />
            </Link>
          )}
          <button
            onClick={() => setIsCollapsed(!isCollapsed)}
            className={`flex items-center justify-center w-8 h-8 rounded-lg text-gray-400 hover:bg-gray-100 hover:text-gray-700 transition ${
              isCollapsed ? "mx-auto" : "ml-auto"
            }`}
          >
            {isCollapsed ? <ChevronRight size={16} /> : <ChevronLeft size={16} />}
          </button>
        </div>

        {/* Customer identity strip */}
        {!isCollapsed && (
          <div className="px-3 py-3 border-b border-gray-200">
            <div className="text-sm font-semibold text-gray-900 truncate">{customer.companyName}</div>
            <div className="text-xs text-gray-500 truncate">{customer.contactName}</div>
          </div>
        )}

        {/* Main Nav */}
        <div className="flex-1 min-h-0 overflow-y-auto py-3 px-2 space-y-1">
          {NAV_ITEMS.map((item) => {
            const Icon = item.icon;
            return (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                title={item.label}
                className={({ isActive }) =>
                  `flex items-center gap-3 rounded-lg px-2 py-2.5 text-sm font-medium transition ${
                    isActive
                      ? "bg-slate-950 text-white"
                      : "text-gray-600 hover:bg-gray-100 hover:text-gray-900"
                  } ${isCollapsed ? "justify-center" : ""}`
                }
              >
                <Icon size={18} className="shrink-0" />
                {!isCollapsed && <span>{item.label}</span>}
              </NavLink>
            );
          })}
        </div>

        {/* Bottom Nav */}
        <div className="shrink-0 border-t border-gray-200 py-3 px-2">
          <button
            onClick={handleLogout}
            title={`Log out (${customer.contactName})`}
            className={`w-full flex items-center gap-3 rounded-lg px-2 py-2.5 text-sm font-medium text-gray-600 hover:bg-gray-100 hover:text-gray-900 transition ${
              isCollapsed ? "justify-center" : ""
            }`}
          >
            <LogOut size={18} className="shrink-0" />
            {!isCollapsed && <span>Log out</span>}
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <div className="flex-1 min-w-0 h-screen overflow-y-auto">
        <Outlet context={{ customer }} />
      </div>
    </div>
  );
}